import React, {useState} from "react";
import {
    datetime_format,
    TripDetails,
    useAddParticipant,
    useDeleteTrip,
    useRemoveParticipant,
    useUpdateTrip
} from "../../api";
import Box from "@mui/material/Box";
import {
    Button, CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle, IconButton, List, ListItem, ListItemText,
    TextField
} from "@mui/material";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import dayjs, {Dayjs} from "dayjs";
import {createUseStyles} from "react-jss";
import {useNavigate} from "react-router-dom";
import {TripViewCard} from "./TripViewCard";
import ClearIcon from '@mui/icons-material/Clear';

const style = createUseStyles({
    form: {
        display: 'flex',
        flexDirection: 'column',
    }
});

interface TripDetailsEditorProps {
    trip_details: TripDetails;
    update_trip(trip: TripDetails): void;
}

export function TripDetailsEditor(props: TripDetailsEditorProps) {
    const trip = props.trip_details.trip;
    const classes = style();
    const navigate = useNavigate();
    const update_trip = useUpdateTrip()[1];
    const delete_trip = useDeleteTrip();
    const remove_participant = useRemoveParticipant(trip.id);
    const add_participant = useAddParticipant(trip.id);
    const [name, set_name] = useState(trip.name);
    const [description, set_description] = useState(trip.description);
    const [start_date, set_start_date] = useState<Dayjs | null>(dayjs(trip.start_date, datetime_format));
    const [end_date, set_end_date] = useState<Dayjs | null>(dayjs(trip.end_date, datetime_format));
    const [is_delete_confirm_open, set_delete_confirm_open] = useState(false);
    const [is_deleting, set_is_deleting] = useState(false);
    const [add_participant_username, set_add_participant_username] = useState('');

    const is_dirty = name !== trip.name || description !== trip.description ||
        start_date?.format(datetime_format) !== trip.start_date || end_date?.format(datetime_format) !== trip.end_date;
    const is_valid = name.length > 0 && description.length > 0 && start_date !== null && end_date !== null;

    function on_update_trip() {
        if (is_valid) {
            update_trip({
                id: trip.id,
                name: name,
                description: description,
                start_date: start_date.format(datetime_format),
                end_date: end_date.format(datetime_format),
                fuel_weight: trip.fuel_weight,
                water_weight: trip.water_weight,
            }, (response) => {
                if (response !== undefined) {
                    props.update_trip({...props.trip_details, trip: response});
                }
            });
        }
    }

    function on_remove_participant(user_id: number) {
        remove_participant(user_id, () => {
            let changes = props.trip_details.participants;
            changes = changes.filter(v => v.user_id !== user_id);
            props.update_trip({...props.trip_details, participants: changes});
        });
    }

    return <TripViewCard flexGrow={1} minWidth={100}>
        <div className={classes.form}>
            <Box padding={1}>
                <TextField required
                           margin="dense"
                           id="name"
                           label="Trip Name"
                           fullWidth
                           variant="standard"
                           value={name}
                           onChange={(e) => set_name(e.target.value)}/>
            </Box>
            <Box padding={1}>
                <TextField required
                           multiline
                           margin="dense"
                           id="description"
                           label="Description"
                           fullWidth
                           variant="standard"
                           value={description}
                           onChange={(e) => set_description(e.target.value)}/>
            </Box>
            <Box padding={1}>
                <DatePicker label="Start Date"
                            value={start_date}
                            onChange={(new_start_date) => set_start_date(new_start_date)}/>
            </Box>
            <Box padding={1}>
                <DatePicker label="End Date"
                            value={end_date}
                            onChange={(new_end_date) => set_end_date(new_end_date)}/>
            </Box>
            <Box padding={1}>
                Trip Participants
                <List>
                    {props.trip_details.participants.map(p => (
                        <ListItem key={p.user_id} secondaryAction={<IconButton edge="end" color="warning" onClick={() => on_remove_participant(p.user_id)}>
                                                                       <ClearIcon/>
                                                                   </IconButton>}>
                            <ListItemText>{p.display_name}</ListItemText>
                        </ListItem>
                    ))}
                </List>
                <TextField margin="dense"
                           id="add_participant_username"
                           label="Add Participant"
                           placeholder="user.name"
                           fullWidth
                           variant="standard"
                           value={add_participant_username}
                           onChange={e => set_add_participant_username(e.target.value)}
                           onBlur={e => add_participant({username: add_participant_username}, (response) => {
                               if (response !== undefined) {
                                   props.update_trip({...props.trip_details, participants: [...props.trip_details.participants, response]});
                                   set_add_participant_username('');
                                }
                           })}
                />
            </Box>
            <Box padding={1}>
                <Button disabled={!is_dirty}
                        fullWidth
                        variant="contained"
                        onClick={() => on_update_trip()}>
                    Update
                </Button>
            </Box>
            <Box padding={1}>
                <Button variant="outlined"
                        fullWidth
                        color="warning"
                        onClick={() => {
                            set_delete_confirm_open(true)
                        }}>
                    Delete Trip
                </Button>
            </Box>
        </div>

        <Dialog open={is_delete_confirm_open}
                onClose={() => set_delete_confirm_open(false)}>
            <DialogTitle>Delete trip {trip.name}?</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Are you sure you want to delete this trip? This action cannot be undone!
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => set_delete_confirm_open(false)}
                        disabled={is_deleting}>No</Button>
                {!is_deleting &&
                    <Button color="warning"
                            onClick={() => {
                                set_is_deleting(true);
                                delete_trip(trip.id, () => {
                                    set_delete_confirm_open(false);
                                    navigate('/trips');
                                });
                            }}>
                        Yes, Delete
                    </Button>
                }
                {is_deleting &&
                    <Button color="warning"
                            disabled
                            endIcon={<CircularProgress size={20}/>}>
                        Yes, Delete
                    </Button>
                }
            </DialogActions>
        </Dialog>
    </TripViewCard>
}